import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    signal,
    Signal,
    ChangeDetectionStrategy,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Item } from "../../pojo/Item";
import { SearchCriteriaService } from "../../services/search/search-criteria/search.criteria.service";
import { AttributeI } from "../../pojo/AttributeI";
import { DropDownItem } from "../../pojo/DropDownItem";
import { SearchCriteriaType } from "../../pojo/SearchCriteriaI";
import { S25Util } from "../../util/s25-util";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { Operators, UnaryOperators } from "../s25-dropdown/single-select/s25.search.operators.dropdown.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25EditableModule } from "../s25-editable/s25.editable.module";
import { S25MultiselectModule } from "../s25-multiselect/s25-multiselect.module";
import { S25TimepickerModule } from "../s25-timepicker/s25.timepicker.module";

@Component({
    selector: "s25-ng-ql-search-advanced-custom-attribute",
    template: `
        @if (isInit) {
            <div>
                <label class="ngBold">Attribute</label>
                <s25-generic-dropdown
                    [items]="items"
                    [focusFirst]="false"
                    [searchEnabled]="true"
                    [placeholder]="'Select a Custom Attribute'"
                    [value]="{ itemId: modelValue.cust_atrb_id }"
                    (valueChange)="onAttributeSet($event)"
                    class="c-margin-bottom--half ngBlock limitWidth"
                />
                @if (modelValue.cust_atrb_id) {
                    <label class="ngBold">Operator</label>
                }
                @if (modelValue.cust_atrb_id && showOperatorDropdown()) {
                    <s25-search-operators-dropdown
                        [attributeTypeId]="modelValue.cust_atrb_type"
                        [type]="type"
                        [(value)]="operator"
                        (valueChange)="onOperatorSet($event)"
                        class="c-margin-bottom--half ngBlock limitWidth"
                    />
                }
                @if (
                    modelValue.relationship_type_id &&
                    (modelValue.relationship_type_id % 100 <= 55 || modelValue.relationship_type_id % 100 === 58) &&
                    modelValue.relationship_type_id < 1000
                ) {
                    <div>
                        <label class="ngBold">Value</label>
                        @if (modelValue.relationship_type_id && searchCriteria) {
                            <div>
                                <s25-ng-multiselect-search-criteria
                                    [type]="searchCriteria"
                                    [modelBean]="{ showResult: true, singleSelect: true }"
                                    [selectedItems]="selectedSearchCriteria"
                                    (changed)="onValueSet($event[0])"
                                ></s25-ng-multiselect-search-criteria>
                            </div>
                        }
                        @if (!searchCriteria) {
                            <div>
                                @switch (modelValue.cust_atrb_type) {
                                    @case ("D") {
                                        <div>
                                            <s25-ng-editable-date
                                                [(val)]="modelValue.itemId"
                                                (valChange)="modelValueChange.emit(modelValue)"
                                            ></s25-ng-editable-date>
                                        </div>
                                    }
                                    @case ("T") {
                                        <div>
                                            <s25-timepicker
                                                [(modelValue)]="modelValue.itemId"
                                                (modelValueChange)="modelValueChange.emit(modelValue)"
                                            ></s25-timepicker>
                                        </div>
                                    }
                                    @case ("E") {
                                        <div>
                                            <s25-ng-editable-date-time
                                                [(val)]="modelValue.itemId"
                                                (valChange)="modelValueChange.emit(modelValue)"
                                            ></s25-ng-editable-date-time>
                                        </div>
                                    }
                                    @case ("N") {
                                        <div>
                                            <input
                                                type="number"
                                                [(ngModel)]="modelValue.itemId"
                                                (ngModelChange)="modelValueChange.emit(modelValue)"
                                            />
                                        </div>
                                    }
                                    @case ("F") {
                                        <div>
                                            <input
                                                type="number"
                                                class="c-input"
                                                [(ngModel)]="modelValue.itemId"
                                                (ngModelChange)="modelValueChange.emit(modelValue)"
                                            />
                                        </div>
                                    }
                                    @case ("R") {
                                        <div>
                                            <input
                                                type="text"
                                                class="c-input"
                                                [(ngModel)]="modelValue.itemId"
                                                (ngModelChange)="modelValueChange.emit(modelValue)"
                                            />
                                        </div>
                                    }
                                    @case ("S") {
                                        <div>
                                            <input
                                                type="text"
                                                class="c-input"
                                                [maxLength]="80"
                                                [(ngModel)]="modelValue.itemId"
                                                (ngModelChange)="modelValueChange.emit(modelValue)"
                                            />
                                        </div>
                                    }
                                }
                            </div>
                        }
                    </div>
                }
            </div>
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, S25DropdownPaginatedModule, S25MultiselectModule, S25EditableModule, S25TimepickerModule],
})
export class S25qlSearchAdvancedCustomAttributeComponent implements OnInit {
    @Input() type: Item.Id;
    @Input() modelValue: CustomAttribute;

    @Output() modelValueChange = new EventEmitter<CustomAttribute>();

    isInit = false;
    items: DropDownItem[];
    searchCriteria: SearchCriteriaType["type"];
    selectedSearchCriteria: DropDownItem[];
    operator: DropDownItem | null;
    showOperatorDropdown = signal(true);

    constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

    async ngOnInit() {
        const data: AttributeI[] = (await SearchCriteriaService.getCustomAttributeItems(this.type)) || [];
        this.items = data.map((obj) => {
            return {
                itemName: obj.itemName,
                itemId: obj.itemId,
                type: obj.itemTypeId,
            };
        });
        if (
            !this.modelValue?.itemId &&
            !UnaryOperators.includes(this.modelValue?.relationship_type_id) &&
            !UnaryOperators.includes(this.modelValue?.relationship_type_id % 100)
        ) {
            this.modelValue = {
                cust_atrb_id: null,
                cust_atrb_name: null,
                cust_atrb_type: null,
                itemId: null,
                relationship_type_id: null,
                relationship_type_name: null,
            };
        } else {
            if (!!Number(this.modelValue.cust_atrb_type))
                this.modelValue.cust_atrb_type = Number(this.modelValue.cust_atrb_type);
            this.setSearchCriteria();
            this.selectedSearchCriteria = [{ itemId: this.modelValue.itemId, itemName: this.modelValue.itemName }];
        }

        if (
            this.modelValue.relationship_type_id % 100 === Operators.Equals &&
            this.modelValue.cust_atrb_type === CustomAttributes.Type.Boolean
        )
            this.modelValue.relationship_type_id =
                this.modelValue.itemId === "T" ? Operators.IsTrue : Operators.IsFalse;

        if (typeof this.modelValue.itemId === "string")
            this.modelValue.itemId = this.modelValue.itemId.replace(/^%(.*?)%$/, "$1");

        const relationship = this.modelValue.relationship_type_id;
        //ANG-7827 operator should remain null if when no relationship is set to avoid a gost value
        this.operator =
            relationship != null ? { itemId: relationship > 1000 ? relationship : relationship % 100 } : null;

        this.isInit = true;
        this.changeDetectorRef.markForCheck();
    }

    onAttributeSet(data: DropDownItem) {
        this.showOperatorDropdown.set(false);
        this.operator = null;
        this.selectedSearchCriteria = [];
        this.modelValue.cust_atrb_id = data.val as number;
        this.modelValue.cust_atrb_name = data.itemName;
        this.modelValue.cust_atrb_type = data.type;
        this.modelValue.relationship_type_id = null;
        this.setSearchCriteria();
        this.showOperatorDropdown.set(true);
        this.modelValueChange.emit(this.modelValue);
    }

    setSearchCriteria() {
        if (!isNaN(this.modelValue.cust_atrb_type as number)) {
            const types: string[] = ["", "", "organizations", "contacts", "locations", "", "resources"];
            this.searchCriteria = types[this.modelValue.cust_atrb_type as number] as SearchCriteriaType["type"];
        } else this.searchCriteria = null;
    }

    onValueSet(item: { itemId: string | number; itemName: string }) {
        this.modelValue.itemId = item.itemId;
        this.modelValue.itemName = item.itemName;
        this.modelValueChange.emit(this.modelValue);
    }

    onOperatorSet(data: DropDownItem) {
        const op_id = S25Util.parseInt(data.val);
        if (op_id === Operators.IsTrue || op_id === Operators.Exists) {
            this.modelValue.itemId = "T";
        } else if (op_id === Operators.IsFalse || op_id === Operators.NotExists) {
            this.modelValue.itemId = "F";
        }

        this.modelValue.relationship_type_id = (data.val as number) + this.type * 100;
        this.modelValue.relationship_type_name = data.itemName;
        this.modelValueChange.emit(this.modelValue);
    }
}

type CustomAttribute = {
    cust_atrb_id: number;
    cust_atrb_name?: string;
    cust_atrb_type: string | number;
    // TODO: consider breaking out a type for unary operators, which need no itemId?
    itemId?: string | number;
    itemName?: string;
    relationship_type_id: number;
    relationship_type_name: string;
};
